import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Navbar from "./components/Navbar";
import AboutSection from "./components/AboutSection";
import ServicesSection from "./components/ServicesSection";
import OurApproach from "./components/OurApproach";
import ProjectGallery from "./components/ProjectGallery";
import FooterContact from "./components/FooterContact";
import theme from "./theme";

function ProjectCard({ title, desc, image }) {
  return (
    <div className="group relative rounded-[32px] overflow-hidden border border-[#1f2937] bg-[#111111]/10 hover:border-amber-500/20 transition-all">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${image})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-95" />
      <div className="relative h-full min-h-[220px] flex flex-col justify-end p-6">
        <h3 className="text-amber-400 text-xs tracking-[0.35em] uppercase mb-2">{title}</h3>
        <p className="text-sm text-gray-300">{desc}</p>
      </div>
    </div>
  );
}

function App() {
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHasLoaded(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AnimatePresence>
        {!hasLoaded && (
          <motion.div
            key="splash-screen"
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#000000] font-sans px-4"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.6, ease: "easeInOut" } }}
          >
            <motion.div
              className="flex flex-col items-center gap-4 text-center"
              exit={{ opacity: 0 }}
            >
              <motion.img
                layoutId="brand-logo"
                src="/Logo2.png"
                alt="The Art and Lore Studio logo"
                className="h-24 w-auto shadow-2xl"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
              />
              <motion.div
                layoutId="brand-text"
                className="text-3xl font-bold uppercase tracking-[0.35em] text-amber-200/90 sm:text-4xl md:text-5xl"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
              >
                The Art and Lore Studio
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Navbar isLoaded={hasLoaded} />

      <motion.div
        className="relative z-10 bg-[#0a0a0a]"
        initial={{ opacity: 0, y: 40 }}
        animate={hasLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
        transition={{ duration: 0.8, delay: 0.45, ease: "easeOut" }}
      >
        <div className="mx-auto max-w-[1400px] px-6 py-12" id="home">
          <div className="grid gap-6 md:grid-cols-4">
            <div className="md:col-span-1">
              <div className="rounded-[24px] border border-[#1f2937] bg-[#0a0a0a] overflow-hidden p-6">
                <section className="space-y-8">
                  <div className="space-y-4">
                    <p className="text-xs uppercase tracking-[0.45em] text-amber-200/90">Featured Project</p>
                    <h1 className="text-4xl font-serif font-semibold leading-tight text-white">A curated living environment built for calm, craft, and cinematic detail.</h1>
                    <p className="text-sm leading-7 text-gray-300">Fine materials, intentional rhythm, and a distinctly layered narrative define the space.</p>
                  </div>
                  <div className="grid gap-4">
                    <div className="rounded-[28px] border border-white/10 bg-[#111111]/90 p-6">
                      <span className="text-xs uppercase tracking-[0.35em] text-amber-200/90">Design Story</span>
                      <h2 className="mt-4 text-2xl font-serif font-semibold text-white">Layered spaces with intentional ritual.</h2>
                      <p className="mt-4 text-sm text-gray-300">We compose spaces through tactile materials, soft geometry, and details that feel both modern and natural.</p>
                    </div>
                    {/* <div className="rounded-[28px] border border-white/10 bg-[#111111]/90 p-6">
                      <span className="text-xs uppercase tracking-[0.35em] text-amber-200/90">Editorial Flow</span>
                      <h3 className="mt-4 text-2xl font-serif font-semibold text-white">Spatial storytelling that reads like art.</h3>
                      <p className="mt-4 text-sm text-gray-300">Subtle proportions and layered composition guide the experience from first impression to finish.</p>
                    </div> */}
                  </div>
                </section>
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="rounded-[24px] border border-[#1f2937] bg-[#0a0a0a] overflow-hidden p-6">
                <section className="space-y-8">
                  <div className="space-y-4">
                    <p className="text-xs uppercase tracking-[0.45em] text-amber-200/90">Featured Work</p>
                    <h2 className="text-4xl font-serif font-semibold leading-tight text-white">A free-flowing gallery of spaces that move like a visual essay.</h2>
                    <p className="text-sm leading-7 text-gray-300">Our focus is the quiet intersection of form and feeling. We craft environments that celebrate the beauty of simplicity, purposeful materials, and the warmth of a home well-lived.</p>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2 auto-rows-min">
                    <ProjectCard title="Material Rhythm" desc="Tactile finishes and sculpted forms." image="/gallery/Image3.jpeg" />
                    <ProjectCard title="Wooden Base" desc="Balanced proportion and soft shadow." image="/gallery/Image5.jpeg" />
                    <ProjectCard title="Lights & Geometry" desc="Clean lines, calm tones and subtle geometry." image="/gallery/Image1.jpeg" />
                    <ProjectCard title="Texture Flow" desc="Seamless flow with texture and warmth." image="/gallery/Image2.jpeg" />
                  </div>
                </section>
              </div>
            </div>

            <div className="md:col-span-1">
              <div className="rounded-[24px] border border-[#1f2937] bg-[#0a0a0a] overflow-hidden p-6 space-y-6">
                <div className="h-80 overflow-hidden rounded-[32px] border border-gray-800/50 bg-[#111111]/80">
                  <img
                    src="/gallery/Image4.jpeg"
                    alt="Feature interior"
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="space-y-4">
                  <h3 className="font-serif text-2xl text-white">Design is in the details.</h3>
                  <p className="text-sm text-gray-400">We craft spaces that reflect who you are and how you live.</p>
                  <a href="#about" className="text-amber-500 flex items-center gap-2">About Us <span className="text-amber-500">→</span></a>
                </div>

              </div>

              <div className="mt-8 rounded-[24px] border border-amber-500/20 bg-[#111111]/80 p-8 text-center shadow-[0_30px_80px_rgba(246,194,109,0.08)]">
                <p className="text-sm uppercase tracking-[0.35em] text-amber-200/80">Ready to begin?</p>
                <h3 className="mt-4 text-1xl font-semibold text-white">Get a quote tailored to your next space project.</h3>
                {/* <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-gray-300">
                  Share your vision and we’ll respond with a bespoke proposal designed for your space, schedule, and style.
                </p> */}
                <a
                  href="#contact"
                  className="mt-8 inline-flex rounded-full border border-amber-300/40 bg-amber-200 px-8 py-3 text-sm font-semibold text-[#0b0b0b] transition hover:bg-amber-300 hover:text-black"
                >
                  Get Quote
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 grid gap-6">
            <div className="rounded-[24px] border border-[#1f2937] bg-[#0a0a0a] overflow-hidden">
              <AboutSection />
            </div>
            <ServicesSection />
            <div className="rounded-[24px] border border-[#1f2937] bg-[#0a0a0a] overflow-hidden">
              <OurApproach />
            </div>
            <div className="rounded-[24px] border border-[#1f2937] bg-[#0a0a0a] overflow-hidden">
              <ProjectGallery />
            </div>
          </div>

          <div className="mt-8 rounded-[24px] border border-[#1f2937] bg-[#0a0a0a] overflow-hidden p-6">
            <div id="contact">
              <FooterContact />
            </div>
          </div>
        </div>
      </motion.div>
    </ThemeProvider>
  );
}

export default App;
